import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";

const FIXED = "FIXED_OWNER_PRICE";
const COST_PLUS = "COST_PLUS_PROFIT_SHARE";
const POST_SALE = "POST_SALE_SETTLEMENT";
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const modeLabel = (m) => (m === COST_PLUS ? "Capital + participación" : "Precio fijo");

// Calcula el reparto de una liquidación para una modalidad/porcentaje dados.
const compute = (mode, sale, cost, pct) => {
  if (mode !== COST_PLUS) return { total: cost, ownerProfit: null, pcProfit: null, gross: null, loss: 0 };
  const gp = Math.round((sale - cost) * 100) / 100;
  if (gp < 0) return { total: sale, ownerProfit: 0, pcProfit: 0, gross: gp, loss: Math.round((cost - sale) * 100) / 100 };
  const op = Math.round(gp * ((Number(pct) || 0) / 100) * 100) / 100;
  return { total: Math.round((cost + op) * 100) / 100, ownerProfit: op, pcProfit: Math.round((gp - op) * 100) / 100, gross: gp, loss: 0 };
};

// Herramienta de corrección auditada de liquidación (reutilizable). Recalcula el snapshot
// financiero de una venta histórica. Permite corregir precio, capital Y la modalidad/%/snapshot
// de ESTA venta (sin tocar la config general del owner). Idempotente, efecto neto.
export function CorrectSaleDialog({ item, isUsed = false, onClose, onSaved }) {
  const s = item.sale || {};
  const snapMode = s.settlement_mode || FIXED;
  const isPost = snapMode === POST_SALE;
  const snapPct = s.profit_share_pct;
  const curSold = s.actual_sale_price ?? s.sale_price ?? item.sale_price ?? 0;
  const curCost = s.owner_purchase_cost ?? s.owner_cost ?? item.owner_cost ?? item.acquisition_cost ?? item._capital ?? 0;
  const beforeCalc = compute(snapMode, Number(curSold), Number(curCost), snapPct);
  const curTotal = s.owner_settlement ?? (snapMode === COST_PLUS ? beforeCalc.total : curCost);

  const [sp, setSp] = useState(String(curSold));
  const [oc, setOc] = useState(String(curCost));
  const [mode, setMode] = useState(isPost ? POST_SALE : snapMode);
  const [pct, setPct] = useState(snapPct != null ? String(snapPct) : "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const isCp = mode === COST_PLUS;
  const nsp = Number(sp) || 0, noc = Number(oc) || 0;
  const after = compute(mode, nsp, noc, pct);
  const endpoint = isUsed ? `/admin/inventory/${item.id}/correct-settlement` : `/admin/new-iphones/${item.id}/correct-settlement`;

  const submit = async (confirmFinancial) => {
    setSaving(true);
    try {
      await adminApi.post(endpoint, {
        sale_price: nsp, owner_cost: isPost ? 0 : noc, reason: reason.trim(),
        settlement_mode: isPost ? POST_SALE : mode, profit_share_pct: isCp ? Number(pct) : null,
        confirm_financial: !!confirmFinancial,
      });
      toast.success(isPost ? "Precio corregido · liquidación posterior sigue pendiente" : "Venta corregida");
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 409 && String(detail).startsWith("CONFIRM_REQUIRED")) {
        const msg = String(detail).replace("CONFIRM_REQUIRED:", "").trim() || "Esta venta puede tener movimientos financieros asociados.";
        if (window.confirm(`${msg}\n\n¿Aplicar la corrección de todas formas?`)) {
          await submit(true);
          return;
        }
      } else {
        toast.error(detail || "No se pudo corregir");
      }
    } finally { setSaving(false); }
  };

  const save = async () => {
    if (!reason.trim()) { toast.error("El motivo de corrección es obligatorio"); return; }
    if (!(nsp >= 0)) { toast.error("Precio inválido"); return; }
    if (!isPost && !(noc >= 0)) { toast.error("Monto inválido"); return; }
    if (!isPost && isCp && !(Number(pct) >= 0 && Number(pct) <= 100)) { toast.error("Indica una participación válida (0-100%)"); return; }
    await submit(false);
  };

  const Row = ({ k, v, strong }) => <div className="flex justify-between gap-2"><span className="text-zinc-500">{k}</span><span className={strong ? "font-semibold text-zinc-900" : "font-medium"}>{v}</span></div>;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="correct-sale-dialog">
        <DialogHeader><DialogTitle>Corregir venta · {item.id}</DialogTitle>
          <DialogDescription>Corrige una operación financiera histórica de ESTA venta. Efecto neto / idempotente: no duplica créditos ni movimientos. No cambia la configuración general del consignador.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 space-y-1 text-xs">
            <Row k="Consignador" v={item.owner_name || s.owner_name || "—"} />
            <Row k="Equipo" v={`${item.model || ""} ${item.capacity || ""}${item.color ? " · " + item.color : ""}`} />
            <Row k="Tipo" v={isUsed ? "USADO" : "NUEVO"} />
            <Row k="Modalidad guardada" v={isPost ? "Liquidación posterior" : modeLabel(snapMode)} />
            {snapMode === COST_PLUS && <Row k="% guardado (snapshot)" v={snapPct != null ? `${snapPct}%` : "—"} />}
          </div>

          <label className="flex flex-col gap-1"><span className="text-xs text-zinc-500">Precio real de venta $</span>
            <Input type="number" min="0" value={sp} onChange={(e) => setSp(e.target.value)} className="h-11 rounded-xl" data-testid="correct-sale-price" /></label>

          {isPost ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="correct-sale-postsale-note">
              Liquidación posterior: se corrige solo el precio real de venta. La venta permanece <b>pendiente de acordar</b> el monto del consignador; no se acredita dinero automáticamente.
            </div>
          ) : (
            <>
              <label className="flex flex-col gap-1"><span className="text-xs text-zinc-500">{isCp ? "Capital / owner_cost $" : "Precio fijo del consignador $"}</span>
                <Input type="number" min="0" value={oc} onChange={(e) => setOc(e.target.value)} className="h-11 rounded-xl" data-testid="correct-sale-cost" /></label>

              <div className="rounded-xl border border-zinc-200 p-3 space-y-2">
                <span className="text-xs font-semibold text-zinc-700">Modalidad de esta venta</span>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setMode(FIXED)} data-testid="correct-sale-mode-fixed"
                    className={`h-10 rounded-lg text-xs font-medium border transition-colors ${!isCp ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}>Precio fijo</button>
                  <button type="button" onClick={() => setMode(COST_PLUS)} data-testid="correct-sale-mode-costplus"
                    className={`h-10 rounded-lg text-xs font-medium border transition-colors ${isCp ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}>Capital + participación</button>
                </div>
                {isCp && (
                  <label className="flex flex-col gap-1 pt-1"><span className="text-xs text-zinc-500">Participación del consignador (%)</span>
                    <Input type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="Ej. 50" className="h-11 rounded-xl" data-testid="correct-sale-pct" /></label>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-zinc-200 p-3 text-xs space-y-1" data-testid="correct-sale-before">
                  <p className="font-semibold text-zinc-400 uppercase text-[10px]">Antes</p>
                  <Row k="Venta" v={money(curSold)} /><Row k="Capital" v={money(curCost)} />
                  <Row k="Modalidad" v={modeLabel(snapMode)} />
                  {snapMode === COST_PLUS && <><Row k="Participación" v={snapPct != null ? `${snapPct}%` : "—"} /><Row k="Ganancia real" v={money(beforeCalc.gross)} /><Row k="Ganancia consig." v={money(beforeCalc.ownerProfit)} /><Row k="Ganancia PuntoCell" v={money(beforeCalc.pcProfit)} /></>}
                  <Row k="Total consig." v={money(curTotal)} strong />
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 text-xs space-y-1" data-testid="correct-sale-after">
                  <p className="font-semibold text-emerald-700 uppercase text-[10px]">Después</p>
                  <Row k="Venta" v={money(nsp)} /><Row k="Capital" v={money(noc)} />
                  <Row k="Modalidad" v={modeLabel(mode)} />
                  {isCp && <><Row k="Participación" v={`${Number(pct) || 0}%`} /><Row k="Ganancia real" v={money(after.gross)} /><Row k="Ganancia consig." v={money(after.ownerProfit)} /><Row k="Ganancia PuntoCell" v={money(after.pcProfit)} /></>}
                  <Row k="Total consig." v={money(after.total)} strong />
                </div>
              </div>
            </>
          )}

          <label className="flex flex-col gap-1"><span className="text-xs text-zinc-500">Motivo de corrección *</span>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej. Precio real de venta era $1.120, no $1.180" className="h-11 rounded-xl" data-testid="correct-sale-reason" /></label>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="h-11 rounded-xl flex-1">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="h-11 rounded-xl flex-1 bg-zinc-900 hover:bg-zinc-800" data-testid="correct-sale-confirm">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar corrección"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
